import React from 'react';
import { Overlay } from 'ol';
import { fromLonLat } from 'ol/proj';
import Popup from 'ol-ext/overlay/Popup';
import { Coordinate } from 'ol/coordinate';

import { MapContext } from '../components/map/map-context';
import { GameDocumentContext } from '../contexts/game-document';
import { createCustomTaskPosition } from '../utils/map/task-position';
import { MapIllustrationContext } from '../components/map/map-illustration-context';

export const useGameTaskPosition = ({
  mapType,
  showTaskTitle
}: {
  mapType: 'openStreetMap' | 'satelliteMap' | 'illustrated' | 'googleMaps';
  showTaskTitle: boolean;
}) => {
  const map = React.useContext(MapContext);
  const mapIllustration = React.useContext(MapIllustrationContext);
  const [gameDocument, setGameDocument] = React.useContext(GameDocumentContext);

  const [taskPopupId, setTaskPopupId] = React.useState<string>();

  const mapContext = React.useMemo(
    () => (mapType === 'illustrated' ? mapIllustration : map),
    [mapType, map, mapIllustration]
  );

  const taskContainerClick = React.useCallback(
    (id: string, action: 'right-click' | 'left-click') => {
      const taskOverlay = mapContext.getOverlayById(id);
      const popupOverlay = mapContext.getOverlayById(
        'task-menu-popup'
      ) as Popup;
      if (!taskOverlay || !popupOverlay) return;

      if (action === 'left-click') {
        popupOverlay.hide();
      }

      if (action === 'right-click') {
        const position = taskOverlay.getPosition();
        popupOverlay.show(position, `<div id="task-menu-popup-${id}"/>`);
        setTaskPopupId(id);
      }
    },
    [map, mapIllustration, gameDocument, mapType, setGameDocument]
  );

  //#region Add task element and its overlay
  const addTaskOverlay = React.useCallback(
    (
      id: string,
      name: string,
      iconUrl: string,
      position: Coordinate | number[],
      isAddTaskMode: boolean = false,
      isVisible = true
    ) => {
      if (map || mapIllustration) {
        const taskExist = mapContext.getOverlayById(id);
        // prevent create task overlay more than once
        if (taskExist) {
          updateTaskOverlay({ id, name, icon: iconUrl });
          updateTaskOverlayPosition(id, position);
          return;
        }

        const taskElement = createCustomTaskPosition({
          id,
          name,
          iconUrl,
          isVisible,
          isAddTaskMode,
          showTasknameVisibility: showTaskTitle,
          onClick: taskContainerClick
        });

        const overlay = new Overlay({
          id,
          position: fromLonLat(position || [0, 0]),
          positioning: 'center-center',
          element: taskElement,
          ...(isAddTaskMode && { className: 'task-pointer-none' })
        });

        mapContext.addOverlay(overlay);
      }
    },
    [mapContext, showTaskTitle, taskContainerClick]
  );

  const removeTaskOverlay = React.useCallback(
    (id: string) => {
      if (map || mapIllustration) {
        const getTask = mapContext.getOverlayById(id);
        // if task not found, return
        if (!getTask) return;

        mapContext.removeOverlay(getTask);
      }
    },
    [mapContext]
  );

  const updateTaskOverlay = React.useCallback(
    ({ id, icon, name }: { id: string; icon?: string; name?: string }) => {
      const taskIcon = document.getElementById(
        `icon-${id}`
      ) as HTMLImageElement;
      if (taskIcon && icon) {
        taskIcon.src = icon;
      }

      const taskName = document.getElementById(
        `taskName-${id}`
      ) as HTMLSpanElement;
      if (taskName && name) {
        taskName.innerHTML = name;
      }
    },
    []
  );

  //#region Update task overlay by its id
  const updateTaskOverlayPosition = React.useCallback(
    (id: string, newPosition: Coordinate | number[]) => {
      const task = mapContext.getOverlayById(id);
      if (task) {
        task.setPosition(fromLonLat(newPosition));
      }
    },
    [mapContext]
  );

  return {
    taskPopupId,
    addTaskOverlay,
    updateTaskOverlay,
    removeTaskOverlay,
    updateTaskOverlayPosition
  };
};
